import { CdkOverlayOrigin } from '@angular/cdk/overlay'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'

import { ColDef, ValueGetterParams } from 'ag-grid-community'
import { SelectItem } from 'primeng/api'

import { Observable, combineLatest } from 'rxjs'
import { map } from 'rxjs/operators'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import {
  ACTION_TYPE_UI,
  ELEMENT_TYPE_UI,
  SECTION_TYPE_UI,
} from '@flowx/designer/modules/core/dictionary/audit.models'
import {
  DefaultActionsColumn,
  placeholderWhenEmpty,
} from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { FlxResourcesPageComponent } from '@flowx/designer/modules/projects/components/resources-page/flx-resources-page.component'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import { ActionsCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/actions-cell/actions-cell.component'
import { DateCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/date-cell/date-cell.component'
import { AuditLogDialogComponent } from '@flowx/designer/shared/components/audit-log-dialog/audit-log-dialog.component'
import { AuditLogsTableComponent } from '@flowx/designer/shared/components/audit-logs-table/audit-logs-table.component'
import { AuditStatusCellComponent } from '@flowx/designer/shared/components/audit-logs-table/cells/status-cell/audit-status-cell.component'
import { DateFilterComponent } from '@flowx/designer/shared/components/audit-logs-table/filters/date-filter/date-filter.component'
import { DropdownFilterComponent } from '@flowx/designer/shared/components/audit-logs-table/filters/dropdown-filter/dropdown-filter.component'
import { TextFilterComponent } from '@flowx/designer/shared/components/audit-logs-table/filters/text-filter/text-filter.component'
import { UsersFilterComponent } from '@flowx/designer/shared/components/audit-logs-table/filters/users-filter/users-filter.component'
import { ApplicationsService } from '@flowx/designer/shared/services/applications.service'
import {
  AuditLog,
  AuditQueryParams,
  ElementTypeEnum,
} from '@flowx/designer/shared/services/audit.service'

@Component({
  selector: 'flx-audit-logs',
  templateUrl: './audit-logs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AuditLogsTableComponent, FlxResourcesPageComponent, CdkOverlayOrigin],
  host: { class: 'h-full' },
})
export class AuditLogsComponent {
  readonly #applicationsService = inject(ApplicationsService)
  #activatedRoute = inject(ActivatedRoute)
  #flxModalService = inject(FlxModalService)
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  #destroyRef = inject(DestroyRef)

  initialQuery = input<AuditQueryParams>()
  resourceName = input('')

  isGlobalAudit = signal<boolean>(this.#activatedRoute.snapshot.data?.globalAudit || false)
  isOrganizationAdminWorkspace = this.#workspacesAndPermissionsStore.isOrganizationAdminWorkspace
  workspaces = this.#workspacesAndPermissionsStore.availableWorkspacesForUser

  applications = signal<SelectItem[]>([])
  applicationVersions = signal<SelectItem[]>([])

  subjectOptions: SelectItem[] = Object.keys(ElementTypeEnum).map(
    (key) =>
      ({
        value: key,
        label: ELEMENT_TYPE_UI[key],
      }) satisfies SelectItem
  )

  auditTableDef = computed<ColDef[]>(() => {
    const isGlobalAudit = this.isGlobalAudit()
    const isOrganizationAdminWorkspace = this.isOrganizationAdminWorkspace()
    const workspaces = this.workspaces()
    const applications = this.applications()
    const applicationVersions = this.applicationVersions()
    const initialQuery = this.initialQuery()

    const baseColumns: ColDef[] = [
      {
        headerName: 'Timestamp',
        field: 'timestamp',
        width: 200,
        flex: 0,
        cellRenderer: DateCellComponent,
        filter: DateFilterComponent,
      },
      ...(isGlobalAudit && isOrganizationAdminWorkspace
        ? [
            {
              headerName: 'Workspace ID',
              width: 165,
              flex: 0,
              field: 'workspaceId',
              valueFormatter: placeholderWhenEmpty,
              filter: initialQuery?.workspaceId ? false : DropdownFilterComponent,
              filterParams: {
                options: workspaces.map((workspace) => ({
                  value: workspace.id,
                  label: workspace.name,
                })),
              },
            },
          ]
        : []),
      {
        headerName: 'User',
        width: 180,
        flex: 0,
        field: 'author',
        filter: UsersFilterComponent,
      },
      {
        headerName: 'Project/Library name',
        field: 'appName',
        minWidth: 165,
        valueFormatter: placeholderWhenEmpty,
        filter: initialQuery?.appId ? false : DropdownFilterComponent,
        filterParams: { options: applications },
      },
      {
        headerName: 'Project/Library version',
        field: 'appVersionId',
        minWidth: 175,
        valueFormatter: placeholderWhenEmpty,
        filter: initialQuery?.appVersionId ? false : DropdownFilterComponent,
        filterParams: { options: applicationVersions },
      },
      ...(!isOrganizationAdminWorkspace
        ? [
            {
              headerName: 'Section',
              flex: 1,
              ...(isGlobalAudit && { width: 132, flex: 0 }),
              field: 'section',
              valueGetter: (params: ValueGetterParams) => SECTION_TYPE_UI[params?.data?.section],
            },
          ]
        : []),
      {
        headerName: 'Subject',
        flex: 1,
        ...(isGlobalAudit && { width: 144, flex: 0 }),
        field: 'element',
        valueGetter: (params: ValueGetterParams) => ELEMENT_TYPE_UI[params?.data?.element],
        filter: DropdownFilterComponent,
        filterParams: { options: this.subjectOptions },
      },
      {
        headerName: 'Subject Identifier',
        field: 'name',
        flex: 1,
        ...(isGlobalAudit && { width: 140, flex: 0 }),
        filter: TextFilterComponent,
      },
      {
        headerName: 'Event',
        flex: 1,
        ...(isGlobalAudit && { width: 100, flex: 0 }),
        valueGetter: (params: ValueGetterParams) => ACTION_TYPE_UI[params?.data?.action],
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 95,
        flex: 0,
        cellRenderer: AuditStatusCellComponent,
        valueGetter: (params: ValueGetterParams) => params?.data,
      },
      {
        ...DefaultActionsColumn,
        cellRenderer: ActionsCellComponent<AuditLog>,
        cellRendererParams: {
          actions: [
            {
              icon: 'ph-eye',
              tooltip: '',
              dataTestId: 'view-details-button',
              clickAction: (auditLog: AuditLog) => this.openDetailsModal(auditLog),
            },
          ],
        },
      },
    ]

    return baseColumns.filter(
      (column) =>
        isGlobalAudit ||
        (!isGlobalAudit &&
          initialQuery?.appVersionId &&
          !['appName', 'section'].includes(column.field)) ||
        (!isGlobalAudit &&
          !initialQuery?.appVersionId &&
          !['appName', 'appVersionId', 'section'].includes(column.field))
    )
  })

  title = computed(() => {
    const resourceName = this.resourceName()
    return resourceName ? `${resourceName} Audit Log` : 'Audit Logs'
  })

  constructor() {
    effect(() => {
      const isGlobalAudit = this.isGlobalAudit()

      if (isGlobalAudit) {
        combineLatest({
          applications: this.getApplicationNames(),
          applicationVersions: this.getApplicationVersions(),
        })
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe(({ applications, applicationVersions }) => {
            this.applications.set(applications)
            this.applicationVersions.set(applicationVersions)
          })
      }
    })
  }

  getApplicationNames(): Observable<{ label: string; value: string }[]> {
    return this.#applicationsService.getApplicationNames().pipe(
      map((applications) =>
        applications.map((application) => ({
          label: application.name,
          value: application.id,
        }))
      )
    )
  }

  getApplicationVersions(): Observable<{ label: string; value: string }[]> {
    return this.#applicationsService.getApplicationVersions().pipe(
      map((applicationVersions) =>
        applicationVersions.map((version) => ({
          label: version,
          value: version,
        }))
      )
    )
  }

  openDetailsModal(auditLog: AuditLog): void {
    this.#flxModalService.show(
      AuditLogDialogComponent,
      {
        closeOnBackdropClick: true,
        backdropClass: 'overlay-backdrop',
      },
      { auditLog }
    )
  }
}
